import React, { ReactNode } from "react";
import "../../design-tokens/tokens/tokens.css";
import "./Button.css";

type ButtonPropsSize = "xs" | "sm" | "md" | "lg";
type ButtonPropsType = "primary" | "secondary" | "tertiary" | "link";
type PrimitiveButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

interface ButtonProps extends PrimitiveButtonProps {
  /** From Figma */
  size?: ButtonPropsSize;
  /** From Figma */
  color?: ButtonPropsType;
  customTestId?: string;
  children?: string | number | ReactNode | undefined | null;
  iconLeft?: ReactNode;
  iconMiddle?: ReactNode;
  iconRight?: ReactNode;
}

const iconPaddingSize = {
  xs: 8,
  sm: 8,
  md: 18,
  lg: 18,
};

const getButtonStyleClasses = ({
  size = "md",
  color = "primary",
  isIconVariant = false,
}: {
  size: ButtonPropsSize;
  color: ButtonPropsType;
  isIconVariant: boolean;
}) => {
  const sizeClass = `sr-button-${isIconVariant ? "icon-" + size : size}`;
  const colorClass = `sr-button-${color}`;
  return sizeClass + " " + colorClass;
};

const getTextPaddingStyle = (size, iconLeft, IconRight) => {
  const paddingSize = iconPaddingSize[size] ?? 8;
  if (iconLeft) {
    return {
      paddingLeft: paddingSize,
    };
  }

  if (IconRight) {
    return {
      paddingRight: paddingSize,
    };
  }

  return {};
};

function Button({
  iconLeft,
  iconRight,
  iconMiddle,
  children,
  className,
  size,
  color,
  disabled,
  customTestId,
  type = "button",
  ...rest
}: ButtonProps) {
  const isIconVariant = !!iconMiddle;
  const styleClasses = getButtonStyleClasses({ size, color, isIconVariant });

  return (
    <button
      data-testid={customTestId || "sr-button"}
      className={`sr-button ${styleClasses} ${className ?? ""}`}
      aria-disabled={disabled}
      disabled={disabled}
      type={type}
      {...rest}
    >
      {iconLeft}
      {iconMiddle || (
        <span
          style={{
            margin: "auto",
            fontFamily: "Roobert",
            ...getTextPaddingStyle(size, iconLeft, iconRight),
          }}
        >
          {children}
        </span>
      )}
      {iconRight}
    </button>
  );
}

export type { ButtonProps };
export { Button };
